// Cross-App Preference (ADR 0098): the end user's device-local language + theme, shared across every
// End-User App (grow-mobile, store, accounts) via one cookie on `.polux.weapps.net` — the same
// parent-domain mechanism as the Audience Bridge Cookie. Default = browser language + system theme; an
// override written in any app propagates to all three. The account's `preferredLanguage` is a deferred
// cross-device layer, NOT the client source of truth.
import { resolveDark, applyTheme, watchSystemTheme, type ThemePref } from './theme'

const PREFS_COOKIE = 'wa_prefs'
const ONE_YEAR = 365 * 24 * 60 * 60

type Prefs = { lang?: string; theme?: ThemePref }

// Prod SSO host (*.polux.weapps.net) → share across the parent domain over HTTPS. Dev (localhost / LAN IP)
// → host-only, non-Secure (a Domain=<ip> attr is invalid; a Secure cookie won't store over plain HTTP).
function isProdSsoHost(): boolean {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'polux.weapps.net' || h.endsWith('.polux.weapps.net')
}
function cookieAttrs(): string {
  return isProdSsoHost() ? '; Domain=.polux.weapps.net; Secure' : ''
}

function readPrefs(): Prefs {
  if (typeof document === 'undefined') return {}
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${PREFS_COOKIE}=`))
  if (!match) return {}
  try {
    const parsed = JSON.parse(decodeURIComponent(match.slice(PREFS_COOKIE.length + 1)))
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writePrefs(next: Prefs) {
  if (typeof document === 'undefined') return
  document.cookie = `${PREFS_COOKIE}=${encodeURIComponent(JSON.stringify(next))}; Path=/; Max-Age=${ONE_YEAR}; SameSite=Lax${cookieAttrs()}`
}

/** The stored theme preference, defaulting to `system` when unset or invalid. */
export function getThemePref(): ThemePref {
  const t = readPrefs().theme
  return t === 'light' || t === 'dark' || t === 'system' ? t : 'system'
}
export function setThemePref(theme: ThemePref) {
  writePrefs({ ...readPrefs(), theme })
}

/** The stored language override (a 2-letter code), or null when unset — callers fall back to the browser. */
export function getLangPref(): string | null {
  const l = readPrefs().lang
  return typeof l === 'string' && l ? l : null
}
export function setLangPref(lang: string) {
  writePrefs({ ...readPrefs(), lang })
}

/** Apply the theme from the shared preference now, and keep it in sync with OS changes while the pref is
 *  `system`. Returns an unsubscribe. The single call every app makes on boot. */
export function applyStoredTheme(): () => void {
  const apply = () => applyTheme(resolveDark(getThemePref()))
  apply()
  return watchSystemTheme(() => {
    if (getThemePref() === 'system') apply()
  })
}

/** Resolve a UI language from the shared preference, else the browser's ordered languages, matched against
 *  `supported`, falling back to `fallback`. The one resolver every app uses. */
export function resolveSharedLang<L extends string>(
  supported: readonly L[],
  fallback: L,
  browserLanguages: readonly string[] = typeof navigator !== 'undefined'
    ? (navigator.languages?.length ? navigator.languages : [navigator.language])
    : [],
): L {
  const candidates = [getLangPref(), ...browserLanguages].filter(Boolean) as string[]
  for (const raw of candidates) {
    const code = raw.slice(0, 2).toLowerCase()
    if ((supported as readonly string[]).includes(code)) return code as L
  }
  return fallback
}
